import { useRef, useEffect } from 'react';
import { BlankModalProps } from './modal.tsx';
import { usePopStateInterceptor } from '../../../utils/hooks/use-pop-state-interceptor.ts';
import { wasKeyPressed } from '../../../utils/keyboard.tsx';

/**
 * Hook for calling onClose on unmount.
 */
export const useOnClose = (isOpen: boolean, onClose?: () => void) => {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const isOpenRef = useRef(isOpen);

  // Call onClose on close without unmount, but only if the modal was open before.
  useEffect(() => {
    if (!isOpen && isOpenRef.current === true) {
      onCloseRef.current?.();
    }
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Call onClose on unmount, but only if the modal is still open.
  useEffect(() => {
    return () => {
      if (isOpenRef.current) {
        onCloseRef.current?.();
      }
    };
  }, []);
};

/**
 * Hook for registering a pop state interceptor, which closes the modal
 * when the browser back button is hit.
 */
export const useModalPopStateInterceptor = (
  isOpen: boolean,
  closeModal: () => void,
  disablePopStateInterceptor: BlankModalProps['disablePopStateInterceptor']
) => {
  usePopStateInterceptor({
    shouldPopStateCancel: () => !disablePopStateInterceptor && isOpen,
    popStateCancelled: closeModal
  });
};

export const useDismissViaEscape = (options: { closeModal: () => void; enabled?: boolean }) => {
  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const { enabled = true } = options;
    if (enabled && wasKeyPressed('Escape', e)) {
      // Prevent multiple modals closing at once
      e.preventDefault();
      e.stopPropagation();
      options.closeModal();
    }
  };

  return { onKeyDown };
};
